import React, { useState } from 'react';
import { Drawer, Form, Input, Select, Switch, Button, Space, message } from 'antd';
import { createSsoIntegration, SsoProvider } from '../../lib/integrationsApi';

const { TextArea } = Input;

interface CreateIntegrationDrawerProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CreateIntegrationDrawer: React.FC<CreateIntegrationDrawerProps> = ({
  visible,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [provider, setProvider] = useState<SsoProvider>('teams');

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const config: any = {};

      if (provider === 'teams') {
        config.clientId = values.teamsClientId;
        config.clientSecret = values.teamsClientSecret;
        config.tenantId = values.teamsTenantId || 'common';
        config.redirectUri = values.teamsRedirectUri;
        config.scopes = values.teamsScopes?.split(',').map((s: string) => s.trim()) || ['User.Read'];
        config.captureTeamContext = values.captureTeamContext !== false;
        config.captureChannelContext = values.captureChannelContext !== false;
      } else if (provider === 'maximo') {
        config.authUrl = values.maximoAuthUrl;
        config.apiUrl = values.maximoApiUrl;
        config.loginType = values.maximoLoginType || 'oauth';
        config.clientId = values.maximoClientId;
        config.clientSecret = values.maximoClientSecret;
      } else if (provider === 'slack') {
        config.clientId = values.slackClientId;
        config.clientSecret = values.slackClientSecret;
        config.redirectUri = values.slackRedirectUri;
        config.scopes = values.slackScopes?.split(',').map((s: string) => s.trim()) || ['users:read', 'chat:write'];
        config.captureWorkspaceContext = values.captureWorkspaceContext !== false;
      }

      await createSsoIntegration({
        name: values.name,
        description: values.description,
        provider,
        isActive: values.isActive !== false,
        config,
      });

      message.success('Integration created successfully');
      form.resetFields();
      onSuccess();
      onClose();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to create integration');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Drawer
      title="Create SSO Integration"
      placement="right"
      onClose={handleCancel}
      open={visible}
      width={600}
      extra={
        <Space>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button type="primary" onClick={handleSubmit} loading={loading}>
            Create Integration
          </Button>
        </Space>
      }
      styles={{
        body: { paddingBottom: 80 }
      }}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ isActive: true, provider: 'teams' }}
        style={{ maxHeight: 'calc(100vh - 180px)', overflowY: 'auto', paddingRight: '12px' }}
      >
        <Form.Item label="Integration Name" name="name" rules={[{ required: true }]}>
          <Input placeholder="e.g., Company Microsoft Teams SSO" />
        </Form.Item>

        <Form.Item label="Description" name="description">
          <TextArea rows={2} placeholder="Optional description" />
        </Form.Item>

        <Form.Item label="Provider" name="provider" rules={[{ required: true }]}>
          <Select onChange={(value) => setProvider(value as SsoProvider)}>
            <Select.Option value="teams">Microsoft Teams</Select.Option>
            <Select.Option value="maximo">IBM Maximo</Select.Option>
            <Select.Option value="slack">Slack</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item label="Active" name="isActive" valuePropName="checked">
          <Switch />
        </Form.Item>

        {provider === 'teams' && (
          <>
            <h3 className="text-base font-semibold mb-3">Microsoft Teams Configuration</h3>
            <Form.Item label="Azure AD Client ID" name="teamsClientId" rules={[{ required: true }]}>
              <Input placeholder="00000000-0000-0000-0000-000000000000" />
            </Form.Item>
            <Form.Item label="Client Secret" name="teamsClientSecret">
              <Input.Password placeholder="Client secret (optional)" />
            </Form.Item>
            <Form.Item label="Tenant ID" name="teamsTenantId">
              <Input placeholder="common (or your tenant ID)" />
            </Form.Item>
            <Form.Item label="Redirect URI" name="teamsRedirectUri" rules={[{ required: true }]}>
              <Input placeholder="https://yourdomain.com/auth" />
            </Form.Item>
            <Form.Item label="Scopes (comma-separated)" name="teamsScopes">
              <Input placeholder="User.Read, email, openid" />
            </Form.Item>
            <Form.Item label="Capture Team Context" name="captureTeamContext" valuePropName="checked" initialValue={true}>
              <Switch />
            </Form.Item>
            <Form.Item label="Capture Channel Context" name="captureChannelContext" valuePropName="checked" initialValue={true}>
              <Switch />
            </Form.Item>
          </>
        )}

        {provider === 'maximo' && (
          <>
            <h3 className="text-base font-semibold mb-3">Maximo Configuration</h3>
            <Form.Item label="Auth URL" name="maximoAuthUrl" rules={[{ required: true }]}>
              <Input placeholder="https://maximo.company.com/maximo/oslc/login" />
            </Form.Item>
            <Form.Item label="API URL" name="maximoApiUrl" rules={[{ required: true }]}>
              <Input placeholder="https://maximo.company.com/maximo/api" />
            </Form.Item>
            <Form.Item label="Login Type" name="maximoLoginType">
              <Select>
                <Select.Option value="oauth">OAuth</Select.Option>
                <Select.Option value="form">Form-based</Select.Option>
                <Select.Option value="basic">Basic Auth</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item label="Client ID (for OAuth)" name="maximoClientId">
              <Input placeholder="Optional" />
            </Form.Item>
            <Form.Item label="Client Secret (for OAuth)" name="maximoClientSecret">
              <Input.Password placeholder="Optional" />
            </Form.Item>
          </>
        )}

        {provider === 'slack' && (
          <>
            <h3 className="text-base font-semibold mb-3">Slack Configuration</h3>
            <Form.Item label="Client ID" name="slackClientId" rules={[{ required: true }]}>
              <Input placeholder="0000000000.0000000000" />
            </Form.Item>
            <Form.Item label="Client Secret" name="slackClientSecret">
              <Input.Password placeholder="Client secret (optional)" />
            </Form.Item>
            <Form.Item label="Redirect URI" name="slackRedirectUri" rules={[{ required: true }]}>
              <Input placeholder="https://yourdomain.com/auth" />
            </Form.Item>
            <Form.Item label="Scopes (comma-separated)" name="slackScopes">
              <Input placeholder="users:read, chat:write" />
            </Form.Item>
            <Form.Item label="Capture Workspace Context" name="captureWorkspaceContext" valuePropName="checked" initialValue={true}>
              <Switch />
            </Form.Item>
          </>
        )}
      </Form>
    </Drawer>
  );
};

export default CreateIntegrationDrawer;
